import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getMyNavigationMenu } from "../../api/navigationMenuService";

import {
    PageHeader,
    Panel,
    KpiCard,
} from "../../components/ui/AdminUI";

import {
    SecondaryButton,
} from "../../components/ui/FormControls";

export default function AdminMissionControlPage() {
    const navigate = useNavigate();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const loadNavigationMenu = async (isRefresh = false) => {
        try {
            if (isRefresh) {
                setRefreshing(true);
            } else {
                setLoading(true);
            }

            setError("");

            const data = await getMyNavigationMenu();

            setItems(
                Array.isArray(data)
                    ? data.filter(
                          (item) =>
                              item &&
                              item.path &&
                              item.title
                      )
                    : []
            );
        } catch (err) {
            console.error(
                "Navigation menu load failed:",
                err
            );

            setItems([]);

            setError(
                err?.response?.data?.message ||
                    err?.message ||
                    "Failed to load the Mission Control menu."
            );
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        loadNavigationMenu();
    }, []);

    const statistics = useMemo(() => {
        const categories = new Set(
            items
                .map((item) => item.category)
                .filter(Boolean)
        );

        const securityModules = items.filter((item) => {
            const value = `${ item.title || "" } ${
    item.category || ""
} `.toLowerCase();

            return (
                value.includes("security") ||
                value.includes("trust") ||
                value.includes("blockchain")
            );
        }).length;

        const logisticsModules = items.filter((item) => {
            const value = `${ item.title || "" } ${
    item.category || ""
} `.toLowerCase();

            return (
                value.includes("logistics") ||
                value.includes("warehouse") ||
                value.includes("delivery") ||
                value.includes("shipment")
            );
        }).length;

        return {
            modules: items.length,
            categories: categories.size,
            security: securityModules,
            logistics: logisticsModules,
        };
    }, [items]);

    const groupedItems = useMemo(() => {
        return items.reduce((groups, item) => {
            const category =
                item.category?.trim() ||
                "General";

            if (!groups[category]) {
                groups[category] = [];
            }

            groups[category].push(item);

            return groups;
        }, {});
    }, [items]);

    const handleOpenModule = (path) => {
        if (!path) {
            setError(
                "This module does not have a valid navigation path."
            );
            return;
        }

        navigate(path);
    };

    return (
        <div className="space-y-6">
            <PageHeader
                title="Admin Mission Control"
                subtitle="Unified control portal for AI, Trust, Blockchain, IoT, Security, Digital Twin, Predictive Logistics and Global Operations."
                action={
                    <SecondaryButton
                        type="button"
                        onClick={() =>
                            loadNavigationMenu(true)
                        }
                        disabled={refreshing}
                    >
                        {refreshing
                            ? "Refreshing..."
                            : "Refresh Modules"}
                    </SecondaryButton>
                }
            />

            {error && (
                <div className="rounded-xl border border-red-300 bg-red-50 p-4 text-red-700">
                    {error}
                </div>
            )}

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
                <KpiCard
                    title="Available Modules"
                    value={statistics.modules}
                    color="blue"
                />

                <KpiCard
                    title="Categories"
                    value={statistics.categories}
                    color="purple"
                />

                <KpiCard
                    title="Security Modules"
                    value={statistics.security}
                    color="green"
                />

                <KpiCard
                    title="Logistics Modules"
                    value={statistics.logistics}
                    color="teal"
                />
            </div>

            {loading ? (
                <Panel
                    title="Mission Control Modules"
                    subtitle="Loading available administrative modules."
                >
                    <div className="flex min-h-48 items-center justify-center">
                        <div className="text-center">
                            <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />

                            <p className="text-slate-500">
                                Loading Mission Control...
                            </p>
                        </div>
                    </div>
                </Panel>
            ) : items.length === 0 ? (
                <Panel
                    title="Mission Control Modules"
                    subtitle="No administrative modules are currently available."
                >
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center text-slate-500">
                        No navigation modules were returned for the current administrator.
                    </div>
                </Panel>
            ) : (
                <div className="space-y-6">
                    {Object.entries(groupedItems).map(
                        ([category, categoryItems]) => (
                            <Panel
                                key={category}
                                title={category}
                                subtitle={`${categoryItems.length} ${categoryItems.length === 1 ? "module" : "modules"
                                    } available in this category.`}
                            >
                                <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
                                    {categoryItems.map((item) => (
                                        <button
                                            key={item.path}
                                            type="button"
                                            onClick={() => handleOpenModule(item.path)}
                                            className="group flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-1 hover:border-blue-300 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        >
                                            <div className="mb-4 flex items-start justify-between gap-4">
                                                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-3xl transition group-hover:bg-blue-100">
                                                    {item.icon || "🧩"}
                                                </div>

                                                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                                                    {item.category || "General"}
                                                </span>
                                            </div>

                                            <h3 className="text-lg font-bold text-slate-900">
                                                {item.title}
                                            </h3>

                                            <p className="mt-2 flex-1 text-sm leading-6 text-slate-600">
                                                {item.description ||
                                                    "Open the administrative module."}
                                            </p>

                                            <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                                                <span className="text-xs font-medium text-slate-400">
                                                    {item.path}
                                                </span>

                                                <span className="font-semibold text-blue-600 transition group-hover:translate-x-1">
                                                    Open →
                                                </span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </Panel>
                        )
                    )}
                </div>
            )}
        </div>
    );
}

